// Market Trends board — extracted so it can render both on its own route
// (/trends) AND embedded inline on the Dashboard.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney } from "../lib/api";
import { Card } from "./ui/card";
import { TrendingUp, TrendingDown, BarChart3, Layers, Building2 } from "lucide-react";

const fmtVol = (v) => {
  if (v == null) return "—";
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}k`;
  return String(Math.round(v));
};

const Pct = ({ v, className = "" }) => (
  <span className={`font-mono-tab ${((v || 0) >= 0) ? "gain" : "loss"} ${className}`}>
    {(v || 0) >= 0 ? "+" : ""}{(v || 0).toFixed(2)}%
  </span>
);

const Row = ({ item, onOpen, valueLabel, value }) => {
  const ticker = item.ticker || item.symbol || "?";
  const letter = ticker.slice(0, 1).toUpperCase();
  // Deterministic hue per ticker → visually distinct letter badges
  let hash = 0;
  for (let i = 0; i < ticker.length; i++) hash = (hash * 31 + ticker.charCodeAt(i)) & 0xffff;
  const hue = hash % 360;
  return (
    <button
      onClick={() => onOpen(item)}
      data-testid={`trend-row-${ticker}`}
      className="flex items-center gap-3 px-3 py-2 text-left hover:bg-secondary/50 rounded-md w-full">
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        {item.image ? (
          <div
            className="rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden"
            style={{ width: 28, height: 28, backgroundColor: `hsl(${hue} 55% 22%)` }}
          >
            <img
              src={item.image}
              alt=""
              className="rounded-full object-contain"
              style={{ width: 22, height: 22 }}
            />
          </div>
        ) : (
          <div
            className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold font-mono-tab"
            style={{ width: 28, height: 28, backgroundColor: `hsl(${hue} 55% 22%)`, color: `hsl(${hue} 85% 78%)` }}
            aria-hidden="true"
          >
            {letter}
          </div>
        )}
        <div className="min-w-0">
          <div className="font-mono-tab font-medium text-sm truncate">{ticker}</div>
          <div className="text-[10px] text-muted-foreground truncate">{item.name}</div>
        </div>
      </div>
      <div className="text-right font-mono-tab text-sm whitespace-nowrap flex-shrink-0 min-w-[72px]">{fmtMoney(item.price)}</div>
      <div className="text-right whitespace-nowrap flex-shrink-0 min-w-[60px]"><Pct v={item.change_pct} /></div>
      <div className="text-right text-[11px] text-muted-foreground font-mono-tab whitespace-nowrap flex-shrink-0 min-w-[76px]">
        <span className="hidden md:inline uppercase text-[9px] tracking-wider mr-1">{valueLabel}</span>{value}
      </div>
    </button>
  );
};

const Panel = ({ icon: Icon, title, badge, badgeClass, children, empty }) => (
  <Card className="p-4 rounded-xl border-border" data-testid={`trends-panel-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 opacity-70" />
        <div className="font-display font-semibold text-sm">{title}</div>
      </div>
      {badge && <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${badgeClass || "bg-secondary text-muted-foreground"}`}>{badge}</span>}
    </div>
    <div className="space-y-0.5">
      {children || <div className="text-xs text-muted-foreground text-center py-6">{empty || "No data."}</div>}
    </div>
  </Card>
);

// `compact` (default true) hides section header / footer that already exists
// on the parent page. Full-page `/trends` sets compact=false to keep the
// "Updated Xm ago" line and give it a bit more spacing.
export default function MarketTrendsBoard({ compact = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get("/markets/trends").then((r) => { if (alive) setData(r.data); })
      .catch(() => setData({ gainers: [], losers: [], most_active: [], options_active: [], sectors: [] }))
      .finally(() => setLoading(false));
    return () => { alive = false; };
  }, []);

  const openAsset = (item) => navigate(`/asset/${item.asset_type || "stock"}/${item.symbol || item.ticker}`);

  if (loading) return (
    <Card className="p-10 text-center border-border rounded-xl text-sm text-muted-foreground" data-testid="trends-board-loading">
      Loading market trends…
    </Card>
  );
  if (!data) return (
    <Card className="p-10 text-center border-border rounded-xl text-sm text-muted-foreground">
      Market trends unavailable. Refresh to retry.
    </Card>
  );
  const sectors = data.sectors || [];
  const maxSectorAbs = Math.max(1, ...sectors.map((s) => Math.abs(s.change_pct || 0)));

  return (
    <div className={compact ? "space-y-4" : "space-y-6"} data-testid="market-trends-board">
      {/* Sector performance bar chart */}
      <Card className="p-4 md:p-5 rounded-xl border-border" data-testid="trends-sectors">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 opacity-70" />
            <div className="font-display font-semibold text-sm">Sector performance today</div>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">SPDR sector ETFs</div>
        </div>
        {sectors.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-6">Sector data unavailable — try again in a minute.</div>
        ) : (
          <div className="space-y-1.5">
            {sectors.map((s) => {
              const p = s.change_pct || 0;
              const w = Math.abs(p) / maxSectorAbs * 100;
              return (
                <button key={s.ticker}
                        onClick={() => navigate(`/asset/stock/${s.ticker}`)}
                        data-testid={`sector-row-${s.ticker}`}
                        className="w-full grid grid-cols-12 items-center gap-2 px-2 py-1.5 hover:bg-secondary/50 rounded-md">
                  <div className="col-span-4 sm:col-span-3 text-left min-w-0">
                    <div className="font-mono-tab text-xs font-medium truncate">{s.name}</div>
                    <div className="text-[10px] text-muted-foreground">{s.ticker}</div>
                  </div>
                  <div className="col-span-5 sm:col-span-7 relative h-5">
                    <div className="absolute inset-y-0 left-1/2 w-px bg-border" />
                    {p >= 0 ? (
                      <div className="absolute left-1/2 top-0.5 bottom-0.5 rounded-r"
                           style={{ width: `${w / 2}%`, backgroundColor: "hsl(var(--gain))" }} />
                    ) : (
                      <div className="absolute right-1/2 top-0.5 bottom-0.5 rounded-l"
                           style={{ width: `${w / 2}%`, backgroundColor: "hsl(var(--loss))" }} />
                    )}
                  </div>
                  <div className="col-span-3 sm:col-span-2 text-right"><Pct v={p} /></div>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel icon={TrendingUp} title="Top gainers" badge="24H" badgeClass="bg-[hsl(var(--gain))]/15 gain"
               empty="No positive movers.">
          {(data.gainers || []).slice(0, 8).map((m) => (
            <Row key={`g-${m.symbol}`} item={m} onOpen={openAsset}
                 valueLabel="vol" value={fmtVol(m.volume)} />
          ))}
        </Panel>
        <Panel icon={TrendingDown} title="Top losers" badge="24H" badgeClass="bg-[hsl(var(--loss))]/15 loss"
               empty="No losers today.">
          {(data.losers || []).slice(0, 8).map((m) => (
            <Row key={`l-${m.symbol}`} item={m} onOpen={openAsset}
                 valueLabel="vol" value={fmtVol(m.volume)} />
          ))}
        </Panel>

        <Panel icon={BarChart3} title="Most active" badge="$ VOLUME"
               empty="No data.">
          {(data.most_active || []).slice(0, 8).map((m) => {
            const dv = m.asset_type === "crypto" ? m.volume : (m.volume || 0) * (m.price || 0);
            return (
              <Row key={`a-${m.symbol}`} item={m} onOpen={openAsset}
                   valueLabel="$ vol" value={fmtVol(dv)} />
            );
          })}
        </Panel>

        <Panel icon={Layers} title="Top options volume" badge="STOCKS"
               empty="Options flow unavailable — feed may be blocked. Try again shortly.">
          {(data.options_active || []).length > 0 && (data.options_active || []).slice(0, 8).map((m) => (
            <Row key={`o-${m.ticker}`} item={{ ...m, symbol: m.ticker }} onOpen={openAsset}
                 valueLabel="contracts" value={fmtVol(m.options_volume)} />
          ))}
        </Panel>
      </div>

      {!compact && (
        <div className="text-[11px] text-muted-foreground text-center">
          Updated {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : "—"}. Cached 5 minutes.
        </div>
      )}
    </div>
  );
}
